import { useMemo } from "react";
import { DatePicker, Select } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { RotateCcw, Search } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  EMPTY_EXPENSE_FILTERS,
  EXPENSE_CATEGORY_OPTIONS,
  EXPENSE_FREQUENCY_OPTIONS,
  EXPENSE_STATUS_OPTIONS,
  FILTER_SELECT_CLASS,
  type ExpenseFilterValues,
} from "./constants";

interface ExpenseFiltersProps {
  year: number;
  monthIndex: number;
  onMonthChange: (year: number, monthIndex: number) => void;
  values: ExpenseFilterValues;
  onChange: (values: ExpenseFilterValues) => void;
}

const LABEL_CLASS =
  "text-[11px] font-medium uppercase tracking-wide text-gray-500";

const toSelectOptions = (values: readonly string[]) =>
  values.map(value => ({ value, label: value }));

const ExpenseFilters = ({
  year,
  monthIndex,
  onMonthChange,
  values,
  onChange,
}: ExpenseFiltersProps) => {
  const monthValue = useMemo(
    () => dayjs(new Date(year, monthIndex, 1)),
    [year, monthIndex]
  );

  const categorySelectOptions = useMemo(
    () => toSelectOptions(EXPENSE_CATEGORY_OPTIONS),
    []
  );

  const handleMonthChange = (value: Dayjs | null) => {
    if (!value) {
      return;
    }
    onMonthChange(value.year(), value.month());
  };

  const handleReset = () => {
    const now = new Date();
    onMonthChange(now.getFullYear(), now.getMonth());
    onChange({ ...EMPTY_EXPENSE_FILTERS });
  };

  return (
    <Card className="border-gray-200 bg-white shadow-sm">
      <CardContent className="p-4">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end">
          <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:flex-1 xl:grid-cols-5">
            <div className="flex w-full min-w-0 flex-col gap-1.5">
              <Label htmlFor="expense-month" className={LABEL_CLASS}>
                Month
              </Label>
              <DatePicker
                id="expense-month"
                picker="month"
                allowClear={false}
                value={monthValue}
                onChange={handleMonthChange}
                format="MMMM, YYYY"
                className="h-9 w-full rounded-lg border-gray-200 bg-[#fafafa]"
                getPopupContainer={trigger =>
                  trigger.parentElement ?? document.body
                }
              />
            </div>

            <div className="flex w-full min-w-0 flex-col gap-1.5">
              <Label htmlFor="expense-category" className={LABEL_CLASS}>
                Category
              </Label>
              <Select
                id="expense-category"
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="All"
                value={values.category ?? undefined}
                onChange={value =>
                  onChange({ ...values, category: value || undefined })
                }
                className={FILTER_SELECT_CLASS}
                style={{ width: "100%" }}
                options={categorySelectOptions}
                notFoundContent="No categories found"
                listHeight={280}
                popupMatchSelectWidth
                getPopupContainer={trigger =>
                  trigger.parentElement ?? document.body
                }
              />
            </div>

            <div className="flex w-full min-w-0 flex-col gap-1.5">
              <Label htmlFor="expense-frequency" className={LABEL_CLASS}>
                Frequency
              </Label>
              <Select
                id="expense-frequency"
                allowClear
                placeholder="All"
                value={values.frequency ?? undefined}
                onChange={value =>
                  onChange({ ...values, frequency: value || undefined })
                }
                className={FILTER_SELECT_CLASS}
                style={{ width: "100%" }}
                options={toSelectOptions(EXPENSE_FREQUENCY_OPTIONS)}
                listHeight={280}
                popupMatchSelectWidth
                getPopupContainer={trigger =>
                  trigger.parentElement ?? document.body
                }
              />
            </div>

            <div className="flex w-full min-w-0 flex-col gap-1.5">
              <Label htmlFor="expense-status" className={LABEL_CLASS}>
                Status
              </Label>
              <Select
                id="expense-status"
                allowClear
                placeholder="All"
                value={values.status ?? undefined}
                onChange={value =>
                  onChange({ ...values, status: value || undefined })
                }
                className={FILTER_SELECT_CLASS}
                style={{ width: "100%" }}
                options={toSelectOptions(EXPENSE_STATUS_OPTIONS)}
                listHeight={280}
                popupMatchSelectWidth
                getPopupContainer={trigger =>
                  trigger.parentElement ?? document.body
                }
              />
            </div>

            <div className="flex w-full min-w-0 flex-col gap-1.5">
              <Label htmlFor="expense-search" className={LABEL_CLASS}>
                Search
              </Label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                <Input
                  id="expense-search"
                  value={values.search}
                  onChange={event =>
                    onChange({ ...values, search: event.target.value })
                  }
                  placeholder="Search expenses..."
                  className="h-9 rounded-lg border-gray-200 bg-[#fafafa] pl-8 text-sm"
                />
              </div>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-9 w-full shrink-0 xl:w-[110px]"
            onClick={handleReset}
          >
            <RotateCcw className="mr-1.5 h-4 w-4" />
            Reset
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ExpenseFilters;
